import { createGaussianData } from "./helpers/GaussianData";
import { createUniformSampler } from "./helpers/GaussianSampling";
import { halfToFloat } from "./helpers/BinaryUtils";
import { clamp01 } from "./helpers/ColorUtils";
import { writeNormalizedQuaternionWXYZ } from "./helpers/QuaternionUtils";

const SPZ_MAGIC = 0x5053474e;
const COLOR_SCALE = 0.15;
const LEGACY_HEADER_SIZE = 16;

/**
 * Loads an SPZ scene.
 *
 * Legacy SPZ versions 1-3 use gzip compression and are decoded here.
 * Version 4 uses ZSTD and is detected but not currently supported.
 */
export async function loadSPZ(buffer, { maxGaussians } = {}) {
  const bytes = new Uint8Array(buffer);

  // Uncompressed NGSP header identifies the newer ZSTD-based format.
  if (
    bytes.length >= 8 &&
    bytes[0] === 0x4e &&
    bytes[1] === 0x47 &&
    bytes[2] === 0x53 &&
    bytes[3] === 0x50
  ) {
    const version = new DataView(buffer).getUint32(4, true);

    throw new Error(
      `SPZ v${version} detected. ` +
        "SPZ v4 ZSTD support has not been implemented yet.",
    );
  }

  // Legacy SPZ files are gzip streams.
  if (bytes.length < 2 || bytes[0] !== 0x1f || bytes[1] !== 0x8b) {
    throw new Error("Invalid or unsupported SPZ file.");
  }

  if (!window.DecompressionStream) {
    throw new Error("This browser does not support gzip decompression.");
  }

  const stream = new Blob([buffer])
    .stream()
    .pipeThrough(new window.DecompressionStream("gzip"));

  const raw = await new Response(stream).arrayBuffer();

  return parseLegacySPZ(raw, {
    maxGaussians,
  });
}

/**
 * Decodes the decompressed SPZ v1-v3 binary layout.
 */
function parseLegacySPZ(buffer, { maxGaussians }) {
  if (buffer.byteLength < LEGACY_HEADER_SIZE) {
    throw new Error("SPZ header is incomplete.");
  }

  const view = new DataView(buffer);
  const magic = view.getUint32(0, true);

  if (magic !== SPZ_MAGIC) {
    throw new Error("Invalid SPZ magic.");
  }

  const version = view.getUint32(4, true);

  if (version < 1 || version > 3) {
    throw new Error(`Unsupported legacy SPZ version ${version}.`);
  }

  const sourceCount = view.getUint32(8, true);

  if (!sourceCount) {
    throw new Error("SPZ contains no Gaussians.");
  }

  const fractionalBits = view.getUint8(13);

  const positionBytesPerComponent = version === 1 ? 2 : 3;

  const positionBytes = sourceCount * 3 * positionBytesPerComponent;

  const alphaOffset = LEGACY_HEADER_SIZE + positionBytes;

  const colorOffset = alphaOffset + sourceCount;

  const scaleOffset = colorOffset + sourceCount * 3;

  const rotationOffset = scaleOffset + sourceCount * 3;

  const sampler = createUniformSampler(sourceCount, maxGaussians);

  const data = createGaussianData(sampler.outputCount, {
    sourceCount,
    sampled: sampler.sampled,
  });

  const positionDivisor = 2 ** fractionalBits;

  for (let outputIndex = 0; outputIndex < data.count; outputIndex++) {
    const sourceIndex = sampler.sourceIndexFor(outputIndex);

    const vectorOffset = outputIndex * 3;

    for (let axis = 0; axis < 3; axis++) {
      const componentIndex = sourceIndex * 3 + axis;

      if (version === 1) {
        data.positions[vectorOffset + axis] = halfToFloat(
          view.getUint16(LEGACY_HEADER_SIZE + componentIndex * 2, true),
        );
      } else {
        const offset = LEGACY_HEADER_SIZE + componentIndex * 3;

        let value =
          view.getUint8(offset) |
          (view.getUint8(offset + 1) << 8) |
          (view.getUint8(offset + 2) << 16);

        // Sign-extend the packed 24-bit position.
        if (value & 0x800000) {
          value -= 0x1000000;
        }

        data.positions[vectorOffset + axis] = value / positionDivisor;
      }
    }

    data.opacities[outputIndex] =
      view.getUint8(alphaOffset + sourceIndex) / 255;

    for (let channel = 0; channel < 3; channel++) {
      const encoded =
        view.getUint8(colorOffset + sourceIndex * 3 + channel) / 255;

      data.colors[vectorOffset + channel] = clamp01(
        (encoded - 0.5) * COLOR_SCALE + 0.5,
      );
    }

    for (let axis = 0; axis < 3; axis++) {
      const encoded = view.getUint8(scaleOffset + sourceIndex * 3 + axis);

      data.scales[vectorOffset + axis] = Math.exp(encoded / 16 - 10);
    }

    if (version === 3) {
      decodeV3Rotation(
        view,
        rotationOffset + sourceIndex * 4,
        data.rotations,
        outputIndex,
      );
    } else {
      decodeLegacyRotation(
        view,
        rotationOffset + sourceIndex * 3,
        data.rotations,
        outputIndex,
      );
    }
  }

  return data;
}

/**
 * Decodes the four-byte "smallest three" quaternion used by SPZ v3.
 * SPZ stores quaternion components as XYZW; normalized output is WXYZ.
 */
function decodeV3Rotation(view, offset, rotations, outputIndex) {
  const packed =
    (view.getUint8(offset) |
      (view.getUint8(offset + 1) << 8) |
      (view.getUint8(offset + 2) << 16) |
      (view.getUint8(offset + 3) << 24)) >>>
    0;

  const largest = (packed >>> 30) & 3;
  let remaining = packed;

  const quaternion = [0, 0, 0, 0];
  let sumSquares = 0;

  for (let component = 3; component >= 0; component--) {
    if (component === largest) {
      continue;
    }

    const magnitude = remaining & 0x1ff;

    const negative = (remaining >>> 9) & 1;

    remaining >>>= 10;

    let value = (1 / Math.sqrt(2)) * (magnitude / 0x1ff);

    if (negative) {
      value = -value;
    }

    quaternion[component] = value;
    sumSquares += value * value;
  }

  quaternion[largest] = Math.sqrt(Math.max(1 - sumSquares, 0));

  writeNormalizedQuaternionWXYZ(
    rotations,
    outputIndex,
    quaternion[3],
    quaternion[0],
    quaternion[1],
    quaternion[2],
  );
}

/**
 * Decodes the three-component quaternion representation used by
 * legacy SPZ v1-v2 and reconstructs the positive W component.
 */
function decodeLegacyRotation(view, offset, rotations, outputIndex) {
  const x = view.getUint8(offset) / 127.5 - 1;

  const y = view.getUint8(offset + 1) / 127.5 - 1;

  const z = view.getUint8(offset + 2) / 127.5 - 1;

  const w = Math.sqrt(Math.max(1 - x * x - y * y - z * z, 0));

  writeNormalizedQuaternionWXYZ(rotations, outputIndex, w, x, y, z);
}
